import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';

export const Reveal = ({ children, delay = 0, className = '', as: Tag = 'div', ...rest }) => {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const io = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisible(true);
          io.disconnect();
        }
      },
      { threshold: 0.12, rootMargin: '0px 0px -40px 0px' }
    );
    io.observe(el);
    return () => io.disconnect();
  }, []);

  return (
    <Tag
      ref={ref}
      className={`reveal ${visible ? 'in' : ''} ${className}`}
      style={{ '--reveal-delay': `${delay}ms` }}
      {...rest}
    >
      {children}
    </Tag>
  );
};

export const useCountUp = (target, duration = 1600) => {
  const ref = useRef(null);
  const [value, setValue] = useState(0);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const io = new IntersectionObserver(([entry]) => {
      if (!entry.isIntersecting) return;
      io.disconnect();
      const start = performance.now();
      const tick = (now) => {
        const p = Math.min((now - start) / duration, 1);
        setValue(Math.round(target * (1 - Math.pow(1 - p, 3))));
        if (p < 1) requestAnimationFrame(tick);
      };
      requestAnimationFrame(tick);
    }, { threshold: 0.4 });
    io.observe(el);
    return () => io.disconnect();
  }, [target, duration]);

  return [ref, value];
};

export const Eyebrow = ({ children, dark = true, className = '' }) => (
  <p className={`font-mono text-xs uppercase tracking-[0.25em] font-bold inline-flex items-center gap-2 ${dark ? 'text-amber' : 'text-energy'} ${className}`}>
    <span className="w-6 h-px bg-current" />
    {children}
  </p>
);

export const SectionHeader = ({ eyebrow, title, subtitle, dark = false, center = false, className = '' }) => (
  <Reveal className={`${center ? 'text-center mx-auto' : ''} max-w-3xl ${className}`}>
    {eyebrow && <Eyebrow dark={dark} className="mb-4">{eyebrow}</Eyebrow>}
    <h2 className={`text-3xl sm:text-4xl lg:text-5xl font-black tracking-tight leading-[1.05] ${dark ? 'text-white' : 'text-ink'}`}>
      {title}
    </h2>
    {subtitle && (
      <p className={`text-base md:text-lg mt-5 leading-relaxed ${dark ? 'text-slate-400' : 'text-slate-600'} ${center ? 'mx-auto' : ''} max-w-2xl`}>
        {subtitle}
      </p>
    )}
  </Reveal>
);

export const PageHero = ({ eyebrow, title, subtitle, image, children, testId }) => (
  <section className="relative overflow-hidden bg-abyss text-white pt-40 pb-24 noise" data-testid={testId}>
    <div className="absolute inset-0 circuit-grid opacity-70 pointer-events-none" />
    {image && (
      <div
        className="absolute inset-y-0 right-0 w-full lg:w-3/5 bg-cover bg-center opacity-40 mask-fade-left pointer-events-none"
        style={{ backgroundImage: `url(${image})` }}
      />
    )}
    <div className="absolute inset-0 bg-gradient-to-t from-abyss via-abyss/60 to-transparent pointer-events-none" />
    <div className="glow-orb w-[420px] h-[420px] bg-energy/20 -bottom-40 -left-20 pointer-events-none" />
    <div className="glow-orb w-[320px] h-[320px] bg-signal/10 -top-20 right-1/4 pointer-events-none" />
    <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <Reveal>
        {eyebrow && <Eyebrow className="mb-6">{eyebrow}</Eyebrow>}
        <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black tracking-tight leading-[1.05] max-w-3xl">{title}</h1>
        {subtitle && <p className="text-base md:text-lg text-slate-300 mt-6 max-w-2xl leading-relaxed">{subtitle}</p>}
        {children}
      </Reveal>
    </div>
  </section>
);

export const BtnPrimary = ({ to, href, children, className = '', icon = true, ...rest }) => {
  const cls = `btn-energy inline-flex items-center justify-center gap-2 font-bold text-white px-7 py-3.5 rounded-xl ${className}`;
  const content = (
    <>
      {children}
      {icon && <ArrowRight size={18} className="transition-transform group-hover:translate-x-1" />}
    </>
  );
  if (href) return <a href={href} className={`group ${cls}`} {...rest}>{content}</a>;
  if (to) return <Link to={to} className={`group ${cls}`} {...rest}>{content}</Link>;
  return <button className={`group ${cls}`} {...rest}>{content}</button>;
};

export const BtnGlass = ({ to, children, className = '', ...rest }) => (
  <Link
    to={to}
    className={`inline-flex items-center justify-center gap-2 font-semibold text-white bg-white/5 hover:bg-white/10 backdrop-blur-xl border border-white/20 hover:border-white/50 px-7 py-3.5 rounded-xl transition-all duration-300 hover:-translate-y-0.5 ${className}`}
    {...rest}
  >
    {children}
  </Link>
);

export const BtnOutline = ({ to, children, className = '', ...rest }) => (
  <Link
    to={to}
    className={`inline-flex items-center justify-center gap-2 font-bold text-ink border-2 border-ink px-7 py-3.5 rounded-xl transition-all duration-300 hover:bg-ink hover:text-white hover:-translate-y-0.5 ${className}`}
    {...rest}
  >
    {children}
  </Link>
);

export const TechCard = ({ dark = false, children, className = '', ...rest }) => (
  <div
    className={`tech-card brackets rounded-2xl p-7 ${
      dark
        ? 'bg-surface border border-white/10 hover:border-energy/50 shadow-[0_10px_30px_rgba(0,0,0,0.3)] hover:shadow-[0_20px_40px_rgba(255,94,20,0.15)]'
        : 'bg-white border border-slate-200/80 hover:border-energy/40 shadow-[0_4px_20px_rgba(0,0,0,0.04)] hover:shadow-[0_20px_40px_rgba(0,0,0,0.08)]'
    } ${className}`}
    {...rest}
  >
    {children}
  </div>
);

export const IconBox = ({ icon: Icon, tone = 'energy', className = '' }) => {
  const tones = {
    energy: 'bg-gradient-to-br from-energy to-[#E65100] shadow-[0_8px_20px_rgba(255,94,20,0.35)]',
    signal: 'bg-gradient-to-br from-signal to-[#0891B2] shadow-[0_8px_20px_rgba(0,229,255,0.25)]',
    ink: 'bg-ink shadow-lg'
  };
  return (
    <div className={`w-12 h-12 rounded-xl flex items-center justify-center text-white transition-transform duration-300 group-hover:scale-110 group-hover:rotate-3 ${tones[tone]} ${className}`}>
      <Icon size={22} />
    </div>
  );
};

export const Stat = ({ value, suffix = '', label, dark = true }) => {
  const [ref, n] = useCountUp(value);
  return (
    <div ref={ref} className="text-left">
      <div className={`font-display text-4xl sm:text-5xl font-black tracking-tight ${dark ? 'text-white' : 'text-ink'}`}>
        {n}
        <span className="text-energy">{suffix}</span>
      </div>
      <p className={`font-mono text-xs uppercase tracking-[0.2em] mt-2 ${dark ? 'text-slate-400' : 'text-slate-500'}`}>{label}</p>
    </div>
  );
};
